/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { center } from '@turf/turf';
import type {
  Feature,
  Geometry,
  GeometryObject,
  Point,
  Position,
} from 'geojson';
import { isString, trim, trimStart } from 'lodash';
import type { TimeSeriesService } from '@opengeoweb/shared';
import { dateUtils } from '@opengeoweb/shared';
import type { TimeSliderStartEndStep } from '@opengeoweb/store';
import { MapFeatureClass } from '@opengeoweb/webmap-react';
import type {
  EDRCollection,
  EDRInstance,
  EDRParameter,
  Parameter,
  PointerLocation,
  SelectParameter,
} from '../components/TimeSeries/types';
import type { TimeseriesCollectionDetail } from '../components/TimeSeriesSelect/utils';
import { COLOR_MAP, REFERENCE_TIME_ID } from '../constants';

export const createUrl = (
  baseUrl: string,
  pathToAdd?: string,
  queryParameters?: [string, string][],
): string => {
  const urlObject = new URL(baseUrl);
  if (pathToAdd) {
    if (urlObject.pathname) {
      urlObject.pathname = `${trim(urlObject.pathname, '/')}/${trimStart(pathToAdd, '/')}`;
    } else {
      urlObject.pathname = trimStart(pathToAdd, '/');
    }
  }
  if (queryParameters) {
    urlObject.search = [
      ...Array.from(urlObject.searchParams.entries()).map(
        ([key, val]) => `${key}=${val}`,
      ),
      ...queryParameters.map(([key, val]) => `${key}=${val}`),
    ].join('&');
  }
  return urlObject.toString();
};

export const supportedOutputFormat = 'CoverageJSON';

export const getBaseQueryParamArray = (
  parameter: Parameter,
  instance: EDRInstance,
  collection: TimeseriesCollectionDetail,
  intervalParam: string,
): [string, string][] => {
  const res: [string, string][] = [];
  res.push(['parameter-name', parameter.propertyName]);

  if (intervalParam) {
    res.push(['datetime', intervalParam]);
  }

  const crs = instance.extent?.spatial.crs || collection.extent?.spatial?.crs;
  if (crs) {
    res.push(['crs', crs]);
  }

  if (parameter.dimension) {
    if (
      parameter.dimension.id &&
      parameter.dimension.id !== REFERENCE_TIME_ID &&
      parameter.dimension.value !== '' &&
      parameter.dimension.value !== undefined
    ) {
      res.push([parameter.dimension.id, parameter.dimension.value]);
    }
  }

  res.push(['f', supportedOutputFormat]);
  return res;
};

export const latestInstance = (
  instances: EDRInstance[],
): EDRInstance | null => {
  if (instances.length === 0) {
    return null;
  }
  if (instances.length === 1) {
    return instances[0];
  }

  return instances.reduce(
    (latest, current) => (latest.id < current.id ? current : latest),
    { id: '' },
  );
};

export const isSupportedInstance = (instances: EDRInstance): boolean => {
  const outputFormatsPosition =
    instances.data_queries?.position?.link.variables.output_formats ?? [];
  const outputFormatsLocation =
    instances.data_queries?.locations?.link.variables.output_formats ?? [];
  return [...outputFormatsLocation, ...outputFormatsPosition].includes(
    supportedOutputFormat,
  );
};

export const listSupportedInstances = (
  instances: EDRInstance[],
): EDRInstance[] => {
  return instances.filter((instance) => isSupportedInstance(instance));
};

export const getUnit = (
  param: EDRParameter,
  lang: string,
  fallbackLang: string,
): string => {
  let unit: string | undefined;

  const symbol = param.unit?.symbol;
  if (isString(symbol)) {
    unit = symbol;
  } else {
    unit = symbol?.value;
  }
  if (unit === undefined) {
    const label = param.unit?.label;
    if (label !== undefined) {
      if (isString(label)) {
        unit = label;
      } else if (label?.[lang]) {
        unit = label?.[lang];
      } else if (label?.[fallbackLang]) {
        unit = label?.[fallbackLang];
      } else {
        const keys = Object.keys(label);
        if (keys.length > 0) {
          unit = label[keys[0]];
        }
      }
    }
  }
  if (unit === undefined) {
    unit = '-?-';
  }
  return unit;
};

export const constructParameterObject = (
  serviceId: string,
  collectionId: string,
  parametersById: EdrParameters,
): SelectParameter[] => {
  return Object.keys(parametersById).map((parameterId) => {
    const parameter = parametersById[parameterId];
    return {
      plotType: 'line',
      propertyName: parameter.id || parameterId,
      ...(parameter?.label ? { propertyLabel: parameter?.label } : {}),
      unit: parameter.unit?.label ?? ' ',
      serviceId,
      collectionId,
      color: COLOR_MAP[parameter.id as keyof typeof COLOR_MAP] ?? 'A',
      opacity: 70,
    };
  });
};

export const getCollectionDetails = (
  service: TimeSeriesService,
  collection: EDRCollection,
  instances?: EDRInstance[],
): TimeseriesCollectionDetail => {
  return {
    serviceId: service.id,
    serviceName: service.name || service.id,
    collectionId: collection.id,
    ...(collection.title !== undefined
      ? { collectionTitle: collection.title }
      : {}),
    ...(collection.description !== undefined
      ? { collectionDescription: collection.description }
      : {}),
    parameters: constructParameterObject(
      service.id,
      collection.id,
      collection.parameter_names as EdrParameters,
    ),
    customDimensions: collection.extent?.custom ?? [],
    dataQueries: collection.data_queries ?? {},
    extent: collection.extent ?? {},
    ...(instances !== undefined ? { allInstances: instances } : {}),
  };
};

export const getServiceCollectionDetails = (
  service: TimeSeriesService,
  collections?: EDRCollection[],
): TimeseriesCollectionDetail[] => {
  const collectionsParameters: TimeseriesCollectionDetail[] = [];
  if (!collections || collections.length === 0) {
    // If no collections are provided, return an empty array
    return collectionsParameters;
  }

  // Only use parameters from collections that support /locations or /position
  for (const collection of collections) {
    if (
      collection.data_queries?.locations ||
      collection.data_queries?.position
    ) {
      collectionsParameters.push(getCollectionDetails(service, collection));
    }
  }

  return collectionsParameters;
};

interface EdrParameter {
  id: string;
  description: string;
  label?: string;
  type: string;
  collectionId: string;
  observedProperty: {
    label: string;
  };
  unit?: {
    label: string;
    symbol: {
      type: string;
      value: string;
    };
  };
}

export type EdrParameters = Record<string, EdrParameter>;

export const getFeatureClass = (
  geometryType: Geometry['type'],
): MapFeatureClass => {
  const featureClasses = {
    Point: MapFeatureClass.ObservationStation,
    MultiPolygon: MapFeatureClass.EDRMultiPolygon,
  };

  return Object.keys(featureClasses).includes(geometryType)
    ? featureClasses[geometryType as keyof typeof featureClasses]
    : MapFeatureClass.EDRFeature;
};

export const getServiceById = (
  services: TimeSeriesService[] | undefined,
  serviceId: string | undefined,
): TimeSeriesService | undefined => {
  return serviceId && services && services.length > 0
    ? services.find((service) => service.id === serviceId)
    : undefined;
};

export const getDetailsForCollectionId = (
  collections: TimeseriesCollectionDetail[],
  collectionId: string,
): TimeseriesCollectionDetail | undefined => {
  return collections.find((collection: TimeseriesCollectionDetail): boolean => {
    return collection.collectionId === collectionId;
  });
};

const HOUR_IN_MILLISECONDS = 3600 * 1000;
/**
 * This function clips the dates in the EDR interval string to a less wide  time range.
 *
 * @param interval EDR interval, e.g. ["startdate_as_isostring","enddate_as_isostring"]
 * @param maxHoursBefore Clips to maximum hours before the given date
 * @param maxHoursAfter Clip to maximum hours after the given date
 * @param date The date to use to clip arround, defaults to Date.now()
 * @returns Clipped EDR interval, e.g. ["startdate_as_isostring","enddate_as_isostring"]
 */
export const clipEdrInterval = (
  interval: string[] | undefined,
  maxHoursBefore = 1 * 24, // 1 day to past
  maxHoursAfter = 10 * 24, // 10 days to the future
  date = Date.now(),
): [string, string] => {
  const startTimeFromEdr = interval?.length && interval[0];
  const endTimeFromEdr = interval?.length && interval[1];

  const startDate = (
    dateUtils.isoStringToDate(String(startTimeFromEdr), false) ??
    dateUtils.getNowUtc()
  ).getTime();

  const endDate = (
    dateUtils.isoStringToDate(String(endTimeFromEdr), false) ??
    dateUtils.getNowUtc()
  ).getTime();

  const clippedStartDate = new Date(
    Math.max(startDate ?? date, date - maxHoursBefore * HOUR_IN_MILLISECONDS),
  );
  const clippedEndDate = new Date(
    Math.min(endDate ?? date, date + maxHoursAfter * HOUR_IN_MILLISECONDS),
  );

  // Round the dates to minutes to make caching more likely.
  clippedStartDate.setMilliseconds(0);
  clippedStartDate.setSeconds(0);
  clippedEndDate.setMilliseconds(0);
  clippedEndDate.setSeconds(0);

  const start = dateUtils.dateToString(clippedStartDate)!;

  const end = dateUtils.dateToString(clippedEndDate)!;

  return [start, end];
};

// 1 day to past, 10 days to future
const clipInterval = [1 * 24, 10 * 24];
export const constructTimeInterval = (
  collection: TimeseriesCollectionDetail | undefined,
  instance: EDRInstance,
  syncgroupTimeState?: TimeSliderStartEndStep,
): string => {
  const interval =
    instance.extent?.temporal?.interval[0] ??
    collection?.extent?.temporal?.interval[0];

  // If syncgroupTimeState is set, use this instead of the fixed timerange.
  if (
    syncgroupTimeState?.start !== undefined &&
    syncgroupTimeState?.end !== undefined
  ) {
    const timeRangeStartIso = dateUtils.dateToString(
      dateUtils.fromUnix(syncgroupTimeState.start),
    );
    const timeRangeEndIso = dateUtils.dateToString(
      dateUtils.fromUnix(syncgroupTimeState.end),
    );
    return `${timeRangeStartIso}/${timeRangeEndIso}`;
  }
  const [start, end] = clipEdrInterval(
    interval,
    clipInterval[0],
    clipInterval[1],
  );
  return `${start}/${end}`;
};

const EARTH_RADIUS = 6371;
/**
 * Find nearest point to the target location in timeseries.
 * Uses equirectangular distance approximation which is not accurate for long
 * distances, but works for finding nearby locations in timeseries.
 *
 * @param targetLocation target location the user has clicked
 * @param pointFeatures features to search through
 * @param maxDistanceKm maximum distance to consider
 * @returns feature nearest to the target location or null if none are within the maximum distance
 */
export const findNearestPoint = (
  targetLocation: PointerLocation,
  pointFeatures: Feature<GeometryObject>[],
  maxDistanceKm = 2,
): Feature<GeometryObject> | null => {
  if (!pointFeatures.length) {
    return null;
  }

  return (
    pointFeatures.reduce<{
      feature: Feature<GeometryObject>;
      distance: number;
    } | null>((nearestPoint, currentFeature) => {
      const [featureLon, featureLat] = getFeatureCenterPoint(currentFeature);

      const lat1Rad = targetLocation.lat * (Math.PI / 180);
      const lat2Rad = featureLat * (Math.PI / 180);
      const lon1Rad = targetLocation.lon * (Math.PI / 180);
      const lon2Rad = featureLon * (Math.PI / 180);

      const x = (lon2Rad - lon1Rad) * Math.cos((lat1Rad + lat2Rad) / 2);
      const y = lat2Rad - lat1Rad;
      const distanceKm = EARTH_RADIUS * Math.sqrt(x * x + y * y);

      if (distanceKm > maxDistanceKm) {
        return nearestPoint;
      }
      if (!nearestPoint || distanceKm < nearestPoint.distance) {
        return { feature: currentFeature, distance: distanceKm };
      }
      return nearestPoint;
    }, null)?.feature ?? null
  );
};

export const getFeatureCenterPoint = (
  feature: Feature<GeometryObject>,
): Position => {
  return feature.geometry.type === 'Point'
    ? (feature.geometry as Point).coordinates
    : center(feature).geometry.coordinates;
};

export const getLocationPositionPath = (
  collectionId: string,
  instanceId: string,
): string => {
  if (instanceId !== '' && instanceId !== collectionId) {
    return `/collections/${collectionId}/instances/${instanceId}`;
  }
  return `/collections/${collectionId}`;
};
